import React from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import Context from "shared/util/Context";
import Timestamp from "apps/messenger/components/MainWindow/MessagePane/Timestamp";
import ProfileLinkRequest from "apps/messenger/containers/ProfileLinkRequest";
import SanitizedText from "shared/components/SanitizedText";
import ProfileComment from "shared/components/ProfileComment";
import Str from "shared/components/Str";
import Strf from "shared/components/Strf";
import { formatReadReceiptTimeStamp } from "apps/messenger/util/helpers";

import {
	SYSTEM_USERID,
	ALICE_USERID,
} from "apps/messenger/util/consts";
import ReadReceiptCheckMarks from "shared/components/svgs/read_receipt.svg";
import UnreadIndicatorIcon from "shared/components/svgs/unread_indicator.svg";

import "apps/messenger/components/MainWindow/MessagePane/Message.scss";

const LegacyReadIndicator = () => (
	<div className="messenger-message-read-indicator">
		<span className="okicon i-checkmark" />
		<Str>Read</Str>
	</div>
);

const DoubleCheckReadIndicator = ({ formattedTimestamp }) => (
	<div className="messenger-message-double-check-read-indicator">
		<Strf
			className="messenger-message-double-check-read-indicator-text"
			vars={{ formattedTimestamp }}
		>
			Read $formattedTimestamp
		</Strf>
		<ReadReceiptCheckMarks />
	</div>
);

DoubleCheckReadIndicator.propTypes = {
	formattedTimestamp: PropTypes.string.isRequired,
};

const UnreadIndicator = () => (
	<div className="messenger-message-double-check-read-indicator">
		<Str
			className="messenger-message-double-check-read-indicator-text"
		>
			Unread
		</Str>
		<UnreadIndicatorIcon />
	</div>
);

const SentIndicator = () => (
	<div className="messenger-message-sent-indicator">
		<Str
			className="messenger-message-sent-indicator-text"
		>
			Sent
		</Str>
	</div>
);

const Message = ({
	timestamp,
	from,
	conversationUserId,
	body,
	profileComment,
	profileLinkRequest,
	showTimestamp,
	showReadIndicator,
	readTimestamp,
	showUnreadIndicator,
	showSentIndicator,
}) => {
	// Is it incoming or outgoing?
	const direction = from === conversationUserId
		? "incoming"
		: "outgoing";

	const messageClasses = classnames(
		"messenger-message",
		`messenger-message--${direction}`,
	);

	const isFromAdmin = from === SYSTEM_USERID || from === ALICE_USERID;

	const isInReadReceiptExperiment = Context.has("READ_RECEIPTS_ALC");

	const ReadReceiptIndicator = !isInReadReceiptExperiment
		? LegacyReadIndicator
		: DoubleCheckReadIndicator;

	return (
		<React.Fragment>
			<div className={messageClasses}>
				{showTimestamp && <Timestamp timestamp={timestamp} />}
				{profileComment && (
					<ProfileComment className="messenger-message-comment" {...profileComment} />
				)}
				{body && (
					<SanitizedText
						className="messenger-message-bubble"
						text={body}
						shouldDangerouslySetInnerHTML={isFromAdmin}
					/>
				)}
				{showReadIndicator && (
					<ReadReceiptIndicator
						formattedTimestamp={formatReadReceiptTimeStamp(readTimestamp)}
					/>
				)}
				{!showReadIndicator && showUnreadIndicator && <UnreadIndicator />}
				{!showReadIndicator && !showUnreadIndicator && showSentIndicator && <SentIndicator />}
			</div>
			{profileLinkRequest && (
				<ProfileLinkRequest {...profileLinkRequest} timestamp={timestamp} />
			)}
		</React.Fragment>
	);
};

Message.propTypes = {
	conversationUserId: PropTypes.string.isRequired,
	from: PropTypes.string.isRequired,

	timestamp: PropTypes.number.isRequired,
	body: PropTypes.string.isRequired,
	profileComment: PropTypes.object,
	profileLinkRequest: PropTypes.object,

	showReadIndicator: PropTypes.bool.isRequired,
	showTimestamp: PropTypes.bool.isRequired,
	readTimestamp: PropTypes.number,
	showUnreadIndicator: PropTypes.bool,
	showSentIndicator: PropTypes.bool,
};

Message.defaultProps = {
	profileComment: null,
	profileLinkRequest: null,
	readTimestamp: null,
	showUnreadIndicator: false,
	showSentIndicator: false,
};

export default Message;



// WEBPACK FOOTER //
// ./src/apps/messenger/components/MainWindow/MessagePane/Message.jsx